import type { DocNode as ADF } from '@atlaskit/adf-schema';
import {
	performDeleteRequest,
	performGetRequest,
	performPostRequest,
	performPutRequest,
} from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { getCmdbCommentByIdUrl } from '@atlassian/jira-servicedesk-cmdb-rest-urls/src/common/constants/comment.tsx';
import {
	getCmdbObjectCommentByIdUrl,
	getCmdbObjectCommentUrl,
} from '@atlassian/jira-servicedesk-cmdb-rest-urls/src/common/constants/object-comment.tsx';
import { getCmdbObjectByIdActivityUrl } from '@atlassian/jira-servicedesk-cmdb-rest-urls/src/common/constants/object.tsx';
import type { ObjectByIdActivityRequest } from '@atlassian/jira-servicedesk-cmdb-rest-urls/src/common/types/object.tsx';
import type {
	WorkspaceId,
	CmdbObjectId,
	CommentRoleType,
} from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/shared-types/index.tsx';
import type {
	AddObjectCommentServiceResponse,
	UpdateObjectCommentServiceResponse,
	DeleteObjectCommentServiceResponse,
} from '../../common/types/timeline-event.tsx';
import { handleTimelineNetworkError } from '../../common/utils/index.tsx';
import type {
	ObjectActivityResponse,
	AddObjectCommentResponse,
	ObjectActivityType,
} from './types.tsx';
import {
	transformAddObjectCommentResponse,
	getRoleTypeNumber,
	transformObjectCommentResponse,
} from './utils.tsx';

export const LOGGER_LOCATION_FETCH_ACTIVITY = 'insight-object.object-timeline.fetchObjectActivity';
export const LOGGER_LOCATION_ADD_COMMENT = 'insight-object.object-timeline.storeObjectComment';
export const LOGGER_LOCATION_UPDATE_COMMENT = 'insight-object.object-timeline.updateObjectComment';
export const LOGGER_LOCATION_DELETE_COMMENT = 'insight-object.object-timeline.deleteObjectComment';

export type FetchObjectActivityParams = {
	workspaceId: WorkspaceId;
	objectId: CmdbObjectId;
	activityTypes?: ObjectActivityType[];
	orderAsc?: boolean;
	startAt: number;
	maxResults: number;
	abortRequestEvent?: Event;
};

export const fetchObjectActivity = async ({
	workspaceId,
	objectId,
	activityTypes,
	orderAsc,
	startAt,
	maxResults,
	abortRequestEvent,
}: FetchObjectActivityParams): Promise<ObjectActivityResponse> => {
	const abortController = new AbortController();

	const abortRequestEventHandler = () => {
		abortController.abort();
	};

	if (abortRequestEvent !== undefined) {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		document.addEventListener(abortRequestEvent.type, abortRequestEventHandler);
	}

	try {
		const queryParams: ObjectByIdActivityRequest = {
			asc: orderAsc,
			types: activityTypes?.join(',') || undefined,
			startAt,
			maxResults,
		};
		const url = getCmdbObjectByIdActivityUrl(workspaceId, objectId, queryParams);
		const response: ObjectActivityResponse = await performGetRequest(url, {
			signal: abortController.signal,
		});
		return response;
	} finally {
		if (abortRequestEvent !== undefined) {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			document.removeEventListener(abortRequestEvent.type, abortRequestEventHandler);
		}
	}
};

export const addObjectComment = async ({
	workspaceId,
	objectId,
	role,
	comment,
}: {
	workspaceId: WorkspaceId;
	objectId: CmdbObjectId;
	role: CommentRoleType;
	comment: ADF;
}): Promise<AddObjectCommentServiceResponse> => {
	try {
		const response: AddObjectCommentResponse = await performPostRequest(
			getCmdbObjectCommentUrl(workspaceId),
			{
				body: JSON.stringify({
					objectId,
					role: getRoleTypeNumber(role),
					body: comment,
				}),
			},
		);
		return transformAddObjectCommentResponse(response);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		return handleTimelineNetworkError(error, LOGGER_LOCATION_ADD_COMMENT);
	}
};

export const editObjectComment = async ({
	workspaceId,
	objectId,
	role,
	commentId,
	comment,
}: {
	workspaceId: WorkspaceId;
	objectId: CmdbObjectId;
	role: CommentRoleType;
	commentId: string;
	comment: ADF;
}): Promise<UpdateObjectCommentServiceResponse> => {
	try {
		const response: AddObjectCommentResponse = await performPutRequest(
			getCmdbObjectCommentByIdUrl(workspaceId, commentId),
			{
				body: JSON.stringify({
					objectId,
					role: getRoleTypeNumber(role),
					body: comment,
				}),
			},
		);
		return { data: transformObjectCommentResponse(response) };
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		return handleTimelineNetworkError(error, LOGGER_LOCATION_UPDATE_COMMENT);
	}
};

export const deleteObjectComment = async ({
	workspaceId,
	commentId,
}: {
	workspaceId: WorkspaceId;
	commentId: string;
}): Promise<DeleteObjectCommentServiceResponse> => {
	try {
		const response = await performDeleteRequest(getCmdbCommentByIdUrl(workspaceId, commentId));
		return response;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		return handleTimelineNetworkError(error, LOGGER_LOCATION_DELETE_COMMENT);
	}
};
